import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from '@mui/material/Backdrop';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
  EmptyState,
  EmptyTitle,
  EmptySubtitle,
  NewChatButton,
  ChatMain,
  SidebarHandle,
} from "./styled";
import { getChatsForUser, createChat, renameChat, getChatDetails } from "../api/chat";
import ChatSidebar from "./ChatSidebar";
import ChatMainView from "./ChatMainView";
import { useChatMessages, MessagesMap  } from "../hooks/useChatMessages";
import { useUser } from "../components/UserContext";
import { ChatSummary, DatabaseInfo } from "../types/chat";
import { fetchMyDatabaseAccess } from '../api/admin';
import { fetchMyDatabases } from '../api/databases';

// Dark theme colors
const DARK_COLORS = {
  PRIMARY: "#000000",
  SECONDARY: "#1a1a1a",
  ACCENT: "#2d2d2d",
  SURFACE: "#121212",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  BORDER: "#333333",
  HOVER: "#404040",
  SUCCESS: "#4caf50",
  ERROR: "#ff4444",
  WARNING: "#ff9800",
  INFO: "#2196f3"
};

const Chat: React.FC = () => {
  const { user } = useUser();
  const userId = user?.id ?? null;
  const username = user?.username ?? null;
  const fullName = user?.fullName ?? null;
  const Email = user?.email ?? null;
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const { chatId: paramChatId } = useParams<{ chatId: string }>();
  const [DBS, setDBS] = useState<DatabaseInfo[]>(() => {
    const stored = localStorage.getItem('userDBS');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }
    return [];
  });

  const [showPopup, setShowPopup] = useState(false);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuChatId, setMenuChatId] = useState<string | null>(null);
  const [menuChatTitle, setMenuChatTitle] = useState<string>("");
  const [isSidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() =>
    JSON.parse(localStorage.getItem("sidebarCollapsed") || "false")
  );
  const avatarAnchorRef = useRef<HTMLDivElement>(null);
  const [dbMenuAnchorEl, setDbMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [menuChat, setMenuChat] = useState<ChatSummary | null>(null);
  const [savedChats, setSavedChats] = useState<ChatSummary[]>([]);
  const [messagesMap, setMessagesMap] = useState<MessagesMap>({});
  const [chatId, setChatId] = useState<string>(paramChatId || "");
  const [isChatsLoaded, setIsChatsLoaded] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => location.state?.justLoggedIn || false);

    useEffect(() => {
      let isMounted = true;
      fetchMyDatabases()
        .then((dbs) => {
          if (!isMounted) return;
          setDBS(dbs);
          localStorage.setItem('userDBS', JSON.stringify(dbs));
        })
        .catch((err) => {
          fetchMyDatabaseAccess()
            .then((dbs) => {
              if (!isMounted) return;
              setDBS(dbs);
              localStorage.setItem('userDBS', JSON.stringify(dbs));
            })
            .catch(() => {});
        });
      return () => { isMounted = false; };
    }, []);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const hasLoadedChats = useRef(false);
  
  useEffect(() => {
    if (!userId || hasLoadedChats.current) return;
    hasLoadedChats.current = true;
    
    let isMounted = true;
    const fetchInitialData = async () => {
      try {
        const apiChats = await getChatsForUser(userId);
        if (!isMounted) return;
        
        const chats: ChatSummary[] = apiChats.map(chat => ({
          id: chat.id,
          chatId: chat.external_id,
          title: chat.title || chat.db_id,
          dbId: chat.db_id,
          dbName: chat.db_id,
        }));
        setSavedChats(chats);
        setIsChatsLoaded(true);
        
        if (!paramChatId && chats.length > 0) {
          navigate(`/chat/${chats[0].chatId}`, { replace: true });
        }
      } catch (error) {
        setIsChatsLoaded(true);
      }
    };
    fetchInitialData();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (!isChatsLoaded) return;
    if (paramChatId) {
      const chatExists = savedChats.some(c => c.chatId === paramChatId);
      if (chatExists) {
        setChatId(paramChatId);
      } else {
        if (savedChats.length > 0) {
          navigate(`/chat/${savedChats[0].chatId}`, { replace: true });
        } else {
          navigate(`/chat`, { replace: true });
        }
      }
    }
  }, [paramChatId, savedChats, isChatsLoaded, navigate]);

  const currentChat = savedChats.find((c) => c.chatId === chatId) || null;
  const numericChatId = currentChat?.id ?? 0;
  const externalId = chatId;
  const targetDbId = currentChat?.dbName ?? (DBS[0] ? ((DBS[0] as any).database_name ?? DBS[0].name ?? "primo_rag") : "primo_rag");
  const chatUniqueId = currentChat?.chatId ?? "unknown";
  
  // Find database numeric ID for ratings
  const currentDb = DBS.find(db => 
    ((db as any).database_name ?? db.name) === targetDbId
  );
  const databaseId = currentDb ? ((currentDb as any).id ?? (currentDb as any).database_id) : undefined;

  const {
    input,
    setInput,
    messages,
    isLoading,
    sendMessage,
    pushMessage ,
    getMessagePair
  } = useChatMessages(
    numericChatId,
    externalId,
    messagesMap,
    setMessagesMap,
    savedChats,
    setSavedChats,
    targetDbId,
    chatUniqueId
  );

  const loadedMessagesChats = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!chatId || !numericChatId) return;
    if (loadedMessagesChats.current.has(chatId)) return;
    
    let isMounted = true;
    const loadChatMessages = async () => {
      try {
        const chatDetails = await getChatDetails(numericChatId);
        if (!isMounted) return;
        
        const messages = (chatDetails.messages || []).map((msg: any) => ({
          id: String(msg.id),
          backendId: typeof msg.id === "number" ? msg.id : Number(msg.id) || undefined,
          text: msg.content,
          type: msg.role,
          timestamp: msg.created_at,
          tableData: msg.output?.result_preview ? {
            headers: Object.keys(msg.output.result_preview[0] || {}),
            rows: msg.output.result_preview,
          } : undefined,
          explanation: msg.output?.explanation,
          isTableExpanded: false,
          sqlQuery: msg.sql_text || msg.output?.sql,
          replyToMessageId: msg.reply_to_message_id ?? msg.parent_message_id ?? undefined,
          useWeb: msg.output?.search_mode === true || msg.search_mode === true || msg.use_web === true
        }));

        setMessagesMap(prev => ({
          ...prev,
          [chatId]: messages,
        }));
        
        loadedMessagesChats.current.add(chatId);
      } catch (error) {
        // Failed to load chat messages
      }
    };
    loadChatMessages();
    return () => {
      isMounted = false;
    };
  }, [chatId, numericChatId]);

  const handleSelectDatabase = useCallback(
    async (db: DatabaseInfo) => {
      if (!userId) return;
      const extId = crypto.randomUUID();
      const api = await createChat(
        extId,
        userId,
        (db as any).database_name ?? db.name
      );
      const summary: ChatSummary = {
        id: api.id,
        chatId: api.external_id,
        title: api.title || ((db as any).database_name ?? db.name),
        dbId: (db as any).id ?? (db as any).database_id,
        dbName: (db as any).database_name ?? db.name,
      };
      setSavedChats((prev) => [summary, ...prev]);
      setMessagesMap((prev) => ({ ...prev, [summary.chatId]: [] }));
      setDBS((prev) => {
        const exists = prev.some(
          (item) => ((item as any).database_name ?? item.name) === ((db as any).database_name ?? db.name)
        );
        if (!exists) {
          const updated = [db, ...prev];
          localStorage.setItem('userDBS', JSON.stringify(updated));
          return updated;
        }
        return prev;
      });
      navigate(`/chat/${summary.chatId}`, { replace: true });
    },
    [userId, navigate]
  );

  const handleRename = async (id: string) => {
    const chat = savedChats.find((c) => c.chatId === id);
    if (!chat) return;
    const updated = savedChats.map((c) =>
      c.chatId === id ? { ...c, title: editTitle } : c
    );
    setSavedChats(updated);
    localStorage.setItem("savedChats", JSON.stringify(updated));
    setEditingChatId(null);
    try {
      await renameChat(chat.id, editTitle);
    } catch (err) {
      alert("Error: " + (err as Error).message);
    }
  };

  const toggleTable = useCallback(
    (msgId: string) => {
      setMessagesMap(prev => ({
        ...prev,
        [chatId]: prev[chatId].map(m =>
          m.id === msgId ? { ...m, isTableExpanded: !m.isTableExpanded } : m
        ),
      }));
    },
    [chatId]
  );

  const handleSend = useCallback(async (replyToId?: string, useWeb?: boolean) => {
    if (chatId && sendMessage) {
      await sendMessage(replyToId, useWeb);
    }
  }, [chatId, sendMessage]);

  const handleMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    chat: ChatSummary
  ) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuChatId(chat.chatId);
    setMenuChatTitle(chat.title);
    setMenuChat(chat);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuChatId(null);
    setMenuChatTitle("");
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const editingChatHandlers = {
    setEditTitle,
    handleRename,
    setEditingChatId,
  };

  const handleMenuRename = () => {
    handleMenuClose();
    setTimeout(() => {
      setEditTitle(menuChatTitle!);
      setEditingChatId(menuChatId!);
    }, 0);
  };

const handleSelectChat = useCallback((id: string) => {
  if (id === chatId) return;
  
  editingChatHandlers.setEditingChatId(null);
  editingChatHandlers.setEditTitle("");
  
  setShowWelcome(false);
  navigate(`/chat/${id}`);
}, [navigate, chatId, editingChatHandlers]);
  return (
    <>
      <Backdrop
        open={!isChatsLoaded}
        sx={{
          color: DARK_COLORS.TEXT_PRIMARY,
          zIndex: theme.zIndex.drawer + 10,
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(45deg, ${DARK_COLORS.PRIMARY}, ${DARK_COLORS.SECONDARY})`,
            borderRadius: '50%',
            padding: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
          }}
        >
          <CircularProgress 
            color="inherit" 
            size={40}
            thickness={3}
            sx={{
              color: DARK_COLORS.TEXT_PRIMARY,
              filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))',
            }}
          />
        </Box>
        <Box sx={{ color: DARK_COLORS.TEXT_SECONDARY, fontSize: '14px', textAlign: 'center' }}>
          Loading your conversations...
        </Box>
      </Backdrop>

      <div style={{ 
        display: "flex", 
        height: "100vh",
        width: "100%",
        backgroundColor: DARK_COLORS.SURFACE,
        overflow: "hidden",
      }}>
        <ChatSidebar
          username={username ?? "Anon"}
          avatar={null}
          email={Email ?? ""}
          avatarAnchorRef={avatarAnchorRef}
          showAvatarSettings={showAvatarMenu}
          setShowAvatarSettings={setShowAvatarMenu}
          onLogout={handleLogout}
          fullName={fullName ?? "Anon"}
          savedChats={savedChats}
          chatId={chatId}
          editingChatId={editingChatId}
          editTitle={editTitle}
          editingChatHandlers={editingChatHandlers}
          menuAnchorEl={menuAnchorEl}
          menuChatId={menuChatId}
          menuChatTitle={menuChatTitle}
          handleMenuOpen={handleMenuOpen}
          handleMenuClose={handleMenuClose}
          handleMenuRename={handleMenuRename}
          handleMenuDelete={() => { }}
          settingsOpen={isSettingsOpen}
          setSettingsOpen={setSettingsOpen}
          availableDatabases={DBS}
          dbMenuAnchorEl={dbMenuAnchorEl}
          handleDbMenuOpen={(e) => setDbMenuAnchorEl(e.currentTarget)}
          handleDbMenuClose={() => setDbMenuAnchorEl(null)}
          handleSelectDatabase={handleSelectDatabase}
          handleSelectChat={handleSelectChat}
          setSavedChats={setSavedChats}
          setMessagesMap={setMessagesMap}
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          navigate={navigate}
        />

        <Box
          onClick={toggleSidebar}
          sx={{
            position: 'fixed',
            top: 16,
            left: isSidebarCollapsed ? '12px' : '240px',
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: DARK_COLORS.PRIMARY,
            color: DARK_COLORS.TEXT_PRIMARY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.6)',
            transition: 'all 0.3s ease',
            border: `1px solid ${DARK_COLORS.BORDER}`,
            zIndex: 1200,
            '&:hover': {
              backgroundColor: DARK_COLORS.SECONDARY,
              transform: 'scale(1.1)',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.8)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <ChevronRightIcon 
            sx={{
              transform: `rotate(${isSidebarCollapsed ? 0 : 180}deg)`,
              transition: 'transform 0.3s ease',
              fontSize: 20,
            }}
          />
        </Box>

        <ChatMain isSidebarCollapsed={isSidebarCollapsed}>
          {isChatsLoaded && savedChats.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${DARK_COLORS.SURFACE} 0%, ${DARK_COLORS.ACCENT} 100%)`,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
                               radial-gradient(circle at 70% 80%, rgba(255, 255, 255, 0.03) 0%, transparent 50%)`,
                  pointerEvents: 'none',
                }
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  maxWidth: 400,
                  mx: 'auto',
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: DARK_COLORS.ACCENT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    border: `2px solid ${DARK_COLORS.BORDER}`,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: '32px',
                      color: DARK_COLORS.TEXT_PRIMARY,
                    }}
                  >
                    💬
                  </Box>
                </Box>
                
                <Box
                  component="h2"
                  sx={{
                    fontSize: '28px',
                    fontWeight: 600,
                    color: DARK_COLORS.TEXT_PRIMARY,
                    marginBottom: 2,
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  Inizia una conversazione
                </Box>
                
                <Box
                  component="p"
                  sx={{
                    fontSize: '16px',
                    color: DARK_COLORS.TEXT_SECONDARY,
                    marginBottom: 4,
                    lineHeight: 1.5,
                  }}
                >
                  Database selezionato: <Box component="strong" sx={{ color: DARK_COLORS.TEXT_PRIMARY }}>{DBS[0]?.name}</Box>
                </Box>
                
                <Box
                  component="button"
                  onClick={() => handleSelectDatabase(DBS[0])}
                  sx={{
                    backgroundColor: DARK_COLORS.PRIMARY,
                    color: DARK_COLORS.TEXT_PRIMARY,
                    border: `1px solid ${DARK_COLORS.BORDER}`,
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.6)',
                    '&:hover': {
                      backgroundColor: DARK_COLORS.SECONDARY,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.8)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  🚀 Crea nuova chat
                </Box>
              </Box>
            </Box>
          ) : (
            <ChatMainView
              chatId={chatId}
              input={input}
              setInput={setInput}
              messages={messages}
              isLoading={isLoading}
              sendMessage={handleSend}
              handleToggleTableExpand={toggleTable}
              isSidebarCollapsed={isSidebarCollapsed}
              toggleSidebar={toggleSidebar}
              showPopup={showPopup}
              setShowPopup={setShowPopup}
              getMessagePair={getMessagePair}
              targetDbId={targetDbId}
              databaseId={databaseId}
            />
          )}
        </ChatMain>
      </div>
    </>
  );
};

export default Chat;
